import { watch, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { matchInternalRoute, toRouteLocation } from '../services/articleLinks'
import { useLazyImages } from './useLazyImages'

export function useArticleContent(containerRef, htmlRef, { linkContext } = {}) {
  const router = useRouter()
  let clickAttached = false

  const { observe: observeLazyImages } = useLazyImages({
    onIntersect: (img) => {
      const src = img.getAttribute('data-src')
      if (!src) return
      img.classList.add('is-loading')
      img.src = src
      img.onload = () => {
        img.classList.remove('is-loading')
        img.classList.add('lazy-loaded')
      }
    }
  })

  const handleClick = (e) => {
    const link = e.target.closest('a')
    if (!link) return

    const hrefAttr = link.getAttribute('href') || link.href
    if (!hrefAttr) return

    if (hrefAttr.startsWith('#')) {
      e.preventDefault()
      const targetId = hrefAttr.slice(1)
      const targetEl = document.getElementById(targetId)
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
        history.replaceState(history.state, '', `#${targetId}`)
      }
      return
    }

    let url
    try {
      url = new URL(hrefAttr, window.location.href)
    } catch {
      return
    }

    const match = matchInternalRoute(url.pathname)
    if (!match) return

    e.preventDefault()
    e.stopImmediatePropagation()
    if (match.kind === 'article' && link.target === '_blank') {
      link.removeAttribute('target')
    }

    const location = toRouteLocation(match, linkContext?.value ?? {})
    if (location) router.push(location)
  }

  const wireAccordions = (root) => {
    root.querySelectorAll('.accordion__item-title').forEach(title => {
      title.addEventListener('click', () => {
        title.classList.toggle('accordion__item-title--active')
        const content = title.nextElementSibling
        if (content) content.classList.toggle('accordion__item-content--visible')
      })
    })
  }

  const wireTabs = (root) => {
    root.querySelectorAll('.tabs-link').forEach(tabLink => {
      tabLink.addEventListener('click', (e) => {
        e.preventDefault()
        const parentTabs = tabLink.closest('.tabs')
        if (!parentTabs) return
        const tabLinks = parentTabs.querySelectorAll('.tabs-link')
        const tabContents = parentTabs.querySelectorAll('.tab')
        const idx = Array.from(tabLinks).indexOf(tabLink)
        const target = tabContents[idx]
        if (!target) return
        tabLinks.forEach(l => l.classList.remove('is-active'))
        tabLink.classList.add('is-active')
        tabContents.forEach(c => c.classList.add('is-hidden'))
        target.classList.remove('is-hidden')
      })
    })
  }

  const wireLazyImages = (root) => {
    const images = root.querySelectorAll('img')
    images.forEach(img => {
      const currentSrc = img.getAttribute('src')
      if (currentSrc && !img.getAttribute('data-src')) {
        img.setAttribute('data-src', currentSrc)
        img.removeAttribute('src')
      }
    })
    observeLazyImages(images)
  }

  watch(htmlRef, () => {
    const root = containerRef.value
    if (!root) return

    if (!clickAttached) {
      root.addEventListener('click', handleClick, true)
      clickAttached = true
    }

    wireAccordions(root)
    wireTabs(root)
    wireLazyImages(root)
  }, { flush: 'post' })

  onBeforeUnmount(() => {
    if (clickAttached && containerRef.value) {
      containerRef.value.removeEventListener('click', handleClick, true)
    }
    clickAttached = false
  })
}
